import React, { Component, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import qs from 'query-string';
// @ts-expect-error - TS2305 - Module '"react-router-dom"' has no exported member 'ContextRouter'.
import type { ContextRouter } from 'react-router-dom';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import { Text } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { IntlShape } from '@atlassian/jira-intl/src/index.tsx';
import type { OrderBy } from '@atlassian/jira-servicedesk-knowledge-services/src/services/rest/articles/index.tsx';
import type {
	Knowledge,
	AppLink,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import type { PushHistory } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-push-provider/index.tsx';
import type { AppLinkError } from '../../../state/entities/applink/types.tsx';
import KnowledgeTable from '../../common/knowledge-table/index.tsx';
import {
	createTitleColumn,
	viewCountColumn,
	lastModifiedColumn,
	LeftAlignedText,
} from '../../common/knowledge-table/knowledge-table-column/view.tsx';
import type { KnowledgeTableColumn } from '../../common/knowledge-table/types.tsx';
import type { KnowledgeTableProps } from '../../common/knowledge-table/view.tsx';
import type { ConnectedKnowledgeHubBulkUpdateCategoriesProps } from '../../knowledge-hub-bulk-update-categories/index.tsx';
import messages from './messages.tsx';

const SPACES_COLUMN_WIDTH = gridSize * 33;
const CATEGORIES_COLUMN_WIDTH = gridSize * 20;

// AllArticlesTable controls the columns, so you shouldn't pass any in to it
type EffectiveKnowledgeTableProps = Partial<
	Flow.Diff<
		KnowledgeTableProps,
		{
			columns: KnowledgeTableProps['columns'];
			categoryId: KnowledgeTableProps['categoryId'];
		}
	>
>;
type OwnProps = {
	KnowledgeHubBulkUpdateCategories: ComponentType<ConnectedKnowledgeHubBulkUpdateCategoriesProps>;
};
export type AllArticlesTableProps = EffectiveKnowledgeTableProps &
	OwnProps & {
		showSpaceColumn: boolean;
		canViewSpaceUrl: boolean;
		appLinkOrError: AppLink | AppLinkError | undefined;
	};

// eslint-disable-next-line jira/react/no-class-components
export class AllArticlesTable extends Component<AllArticlesTableProps> {
	static defaultProps = {
		categoriesSupported: true,
		onSetPage: undefined,
	};

	onSetPage = (
		pageNum: number,
		sortBy: OrderBy,
		push: PushHistory,
		history: ContextRouter['history'],
		analyticsEvent: UIAnalyticsEvent,
		cursor?: string,
		searchQuery?: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		spaceKeys?: Record<any, any>,
	) => {
		// go/jfe-eslint
		const { onSetPage } = this.props;
		onSetPage && onSetPage(pageNum, sortBy, push, history, analyticsEvent, cursor);
		const spacekeyNames = isEmpty(spaceKeys) ? null : JSON.stringify(spaceKeys);
		push
			? push(
					`/knowledge/browse/page/${pageNum}?${qs.stringify(
						{
							cursor,
							sortBy,
							searchQuery,
							spaceKey: spacekeyNames,
						},
						// @ts-expect-error - TS2345 - Argument of type '{ skipNull: boolean; skipEmptyString: boolean; }' is not assignable to parameter of type 'StringifyOptions'.
						{ skipNull: true, skipEmptyString: true },
					)}`,
				)
			: history.push(
					`/knowledge/browse/page/${pageNum}?${qs.stringify(
						{
							cursor,
							sortBy,
							searchQuery,
							spaceKey: spacekeyNames,
						},
						// @ts-expect-error - TS2345 - Argument of type '{ skipNull: boolean; skipEmptyString: boolean; }' is not assignable to parameter of type 'StringifyOptions'.
						{ skipNull: true, skipEmptyString: true },
					)}`,
				);
	};

	getTableColumns(): KnowledgeTableColumn[] {
		const { showSpaceColumn } = this.props;

		return [
			createTitleColumn((knowledge) => {
				const { spaceKey, id } = knowledge;
				return spaceKey !== undefined
					? `/knowledge/articles/${id}?spaceKey=${spaceKey}`
					: `/knowledge/articles/${id}`;
			}),
			...(showSpaceColumn ? [this.getLinkedSpacesColumn()] : []),
			this.getCategoriesColumn(),
			viewCountColumn,
			lastModifiedColumn,
		];
	}

	getCategoriesColumn(): KnowledgeTableColumn {
		// go/jfe-eslint
		const { KnowledgeHubBulkUpdateCategories, categoriesSupported } = this.props;

		return {
			key: 'article-categories',
			header: (intl: IntlShape) => ({
				content: (
					<FlexLeftAlignedText>
						{intl.formatMessage(messages.categoriesHeader)}
						{!categoriesSupported && (
							<NoCategoriesWrapper>
								<Tooltip content={intl.formatMessage(messages.noCategoriesTooltip)}>
									<EditorPanelIcon label="" size="medium" />
								</Tooltip>
							</NoCategoriesWrapper>
						)}
					</FlexLeftAlignedText>
				),
				isSortable: false,
				width: `${CATEGORIES_COLUMN_WIDTH}px`,
			}),
			row: (intl: IntlShape, index: number, knowledge: Knowledge) =>
				!categoriesSupported ? (
					<Text as="em">{intl.formatMessage(messages.noCategoriesRow)}</Text>
				) : (
					<KnowledgeHubBulkUpdateCategories
						key={`article-bulk-categories-${knowledge.id}`}
						articleId={knowledge.id}
						categoryEditorWidth={CATEGORIES_COLUMN_WIDTH}
					/>
				),
		};
	}

	getLinkedSpacesColumn(): KnowledgeTableColumn {
		return {
			key: 'article-linked-spaces',
			header: (intl: IntlShape) => ({
				content: (
					<LeftAlignedText>{intl.formatMessage(messages.linkedSpacesHeader)}</LeftAlignedText>
				),
				isSortable: false,
				width: `${SPACES_COLUMN_WIDTH}px`,
			}),
			row: (intl: IntlShape, index: number, knowledge: Knowledge) => {
				const spaceName = knowledge.spaceName || '';
				const spaceUrl = knowledge.spaceUrl || '';
				const { canViewSpaceUrl } = this.props;
				return canViewSpaceUrl ? (
					<LeftAlignedText>
						<Tooltip content={intl.formatMessage(messages.tooltipText)}>
							<a href={spaceUrl} target="_blank" rel="noopener noreferrer nofollow">
								{spaceName}
							</a>
						</Tooltip>
					</LeftAlignedText>
				) : (
					<LeftAlignedText>{spaceName}</LeftAlignedText>
				);
			},
		};
	}

	render() {
		const { knowledgePieces } = this.props;

		return knowledgePieces ? (
			<KnowledgeTable
				{...this.props}
				categoryId="all-articles"
				actions={[]}
				columns={this.getTableColumns()}
				onSetPage={this.onSetPage}
			/>
		) : (
			<SpinnerContainer>
				<Spinner size={70} />
			</SpinnerContainer>
		);
	}
}

export default AllArticlesTable;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NoCategoriesWrapper = styled.div({
	marginTop: token('space.negative.050', '-4px'),
	marginRight: 0,
	marginBottom: 0,
	marginLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexLeftAlignedText = styled.div({
	textAlign: 'left',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	marginTop: token('space.500', '24px'),
});
